import { observer } from 'mobx-react-lite'
import React, { FC, useMemo } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import Hotkeys from 'react-hot-keys'
import { styled } from '../../lib/stitches'
import { useTreeState } from '../../stores/Root'
import { Box, Column, Row } from '../core/Components'
import useStudio from '../studio/hooks/useStudio'
import { ClipboardContext } from './Context'
import DiscoverStemsBottomDrawer from './DiscoverStemsBottomDrawer'
import SelectedTrackContainer from './SelectedTrackContainer'
import StudioActivityBar, { ActivityBarContent } from './StudioActivityBar'
import StudioAuthBlocker from './StudioAuthBlocker'
import StudioHeader from './StudioHeader'
import StudioSideDrawer from './StudioSideDrawer'
import TrackTable from './TrackTable'

export const RECORD_MARKER_HEIGHT = '30px'
export const FIRST_COLUMN_WIDTH = '365px'
export const ACTIVITY_BAR_WIDTH = '60px'
export const SIDE_DRAWER_WIDTH = '300px'
export const HEADER_HEIGHT = '64px'

const TrackTableContainer = styled(Column, {
  width: '100%',
  height: '100%'
})

const StudioContainer = styled(Row, {
  fontFamily: "'DM Sans', 'Poppins', sans-serif",
  fontWeight: '400',
  backgroundColor: '$gray01',
  height: '100vh',
  width: '100vw',
  overflow: 'hidden'
})

export const CurrentlyPlayingContext = React.createContext(null)
export const CurrentlyPlayingTypes = {
  STEM: 0,
  SONG: 1,
  SAMPLE: 2
}

const Studio: FC = () => {
  const { studio, hotkeysDisabled } = useTreeState()
  const {
    handleHotkeys,
    currentlyPlaying,
    updateCurrentlyPlaying,
    setSideDrawerContent,
    pullOutSideDrawer,
    selectedActivityBarIcon,
    sideDrawerContent,
    selectedTrackContainerHeight,
    instrumentLoopWidthPX,
    zoomIn,
    zoomOut,
    clipboard,
    setClipboard,
    handleSelectedContainerResize
  } = useStudio()

  const studioBodyWidth = useMemo(() => {
    return (
      'calc(100vw - ' +
      ACTIVITY_BAR_WIDTH +
      (sideDrawerContent ? ' - ' + SIDE_DRAWER_WIDTH : '') +
      ')'
    )
  }, [sideDrawerContent])

  const trackTableHeight = useMemo(() => {
    return (
      'calc(100vh - ' +
      HEADER_HEIGHT +
      (studio.selectedStemIndex > -1 || studio.showDiscoverStemsDrawer
        ? ' - ' + selectedTrackContainerHeight
        : '') +
      ')'
    )
  }, [studio.selectedStemIndex, studio.showDiscoverStemsDrawer, selectedTrackContainerHeight])

  const selectedTrackContainerWidth = useMemo(() => {
    return (
      'calc(100vw - ' +
      ACTIVITY_BAR_WIDTH +
      (sideDrawerContent ? ' - ' + SIDE_DRAWER_WIDTH : '') +
      ')'
    )
  }, [sideDrawerContent])

  const keybinds = [
    'space',
    'command+z',
    'control+z',
    'command+y',
    'control+y',
    'shift+command+z',
    'shift+control+z',
    'enter',
    'delete'
  ]

  return (
    <Hotkeys
      allowRepeat={true}
      keyName={keybinds.join(',')}
      onKeyDown={handleHotkeys}
      disabled={hotkeysDisabled}
    >
      <DndProvider backend={HTML5Backend}>
        <CurrentlyPlayingContext.Provider value={[currentlyPlaying, updateCurrentlyPlaying]}>
          <ClipboardContext.Provider value={{ clipboard, setClipboard }}>
            <StudioAuthBlocker />
            <StudioContainer>
              <StudioActivityBar
                setSideDrawerContent={setSideDrawerContent}
                pullOutSideDrawer={pullOutSideDrawer}
                selectedActivityBarIcon={selectedActivityBarIcon}
              />
              <StudioSideDrawer content={sideDrawerContent} />
              <Box style={{ width: studioBodyWidth, height: '100vh' }}>
                <StudioHeader />
                <TrackTableContainer>
                  <TrackTable
                    height={trackTableHeight}
                    openSampleDrawer={() =>
                      pullOutSideDrawer(
                        ActivityBarContent.SAMPLES.component,
                        ActivityBarContent.SAMPLES.title
                      )
                    }
                    instrumentLoopWidthPX={instrumentLoopWidthPX}
                    zoomIn={zoomIn}
                    zoomOut={zoomOut}
                  />
                  {studio.selectedStemIndex > -1 && (
                    <SelectedTrackContainer
                      onResize={handleSelectedContainerResize}
                      height={selectedTrackContainerHeight}
                      close={() => studio.unselectStem()}
                      width={selectedTrackContainerWidth}
                    />
                  )}
                  {studio.showDiscoverStemsDrawer && studio.selectedStemIndex === -1 && (
                    <DiscoverStemsBottomDrawer
                      onResize={handleSelectedContainerResize}
                      height={selectedTrackContainerHeight}
                      width={selectedTrackContainerWidth}
                    />
                  )}
                </TrackTableContainer>
              </Box>
            </StudioContainer>
          </ClipboardContext.Provider>
        </CurrentlyPlayingContext.Provider>
      </DndProvider>
    </Hotkeys>
  )
}

export default observer(Studio)
